import { roundToDecimals } from '../../../utils/numberUtils';
import { CAPACITY_UNKNOWN_PLACEHOLDER } from '../../../constants/capacityConstants';
import { evaluateKpiSeverity } from './kpiSeverity';
import type { KpiSeverity, KpiThreshold } from './types';
import styles from './KpiDistributionPopover.module.css';

interface DistributionBarRowProps {
  name: string;
  planned: number;
  // Absent while the available amount is unknown: the planned bar still
  // renders, but no delta is judged.
  threshold?: KpiThreshold;
  // Text inside the planned bar and in the available column, already formatted
  // in the resource's unit.
  plannedLabel: string;
  thresholdLabel?: string;
  // Decimal places the delta is rounded to.
  deltaDecimals: number;
}

const deltaClass = (severity: KpiSeverity): string => {
  switch (severity) {
    case 'OK':
      return styles.deltaOk;
    case 'WARNING':
      return styles.deltaWarning;
    case 'EXCEEDED':
      return styles.deltaError;
    default: {
      const _exhaustive: never = severity;
      throw new Error(`Unhandled KpiSeverity: ${_exhaustive as string}`);
    }
  }
};

const formatDelta = (delta: number, decimals: number): string => {
  const rounded = roundToDecimals(delta, decimals);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
};

// One resource in a KPI popover's four-column grid: name, planned-against-
// available bars, the available amount, and the difference between the two.
// Renders its cells without a wrapper so they sit directly in the grid.
export const DistributionBarRow = ({
  name,
  planned,
  threshold,
  plannedLabel,
  thresholdLabel,
  deltaDecimals
}: DistributionBarRowProps) => {
  // Per-row local scale: the longer of planned/available maps to the full
  // grid-cell width. The other shrinks proportionally. Both can be 0 (nothing
  // planned against nothing available), so guard the division rather than
  // rendering NaN% widths.
  const maxValue = Math.max(planned, threshold?.limit ?? 0);
  const toPercent = (value: number): number => (maxValue > 0 ? (value / maxValue) * 100 : 0);

  return (
    <>
      <div className={styles.name}>{name}</div>
      <div className={styles.barWrapper}>
        {threshold && (
          <div className={styles.lavender} style={{ width: `${toPercent(threshold.limit)}%` }} />
        )}
        {/* At 0 planned the bar collapses to a padding-wide nub with clipped
            text, so it is omitted entirely. */}
        {planned > 0 && (
          <div className={styles.blue} style={{ width: `${toPercent(planned)}%` }}>
            {plannedLabel}
          </div>
        )}
      </div>
      <div className={styles.threshold}>
        {threshold ? thresholdLabel : CAPACITY_UNKNOWN_PLACEHOLDER}
      </div>
      {/* Without an available amount there is no delta to judge; the empty
          cell keeps the 4-column grid aligned. */}
      {threshold ? (
        <div
          className={`${styles.delta} ${deltaClass(evaluateKpiSeverity(planned, threshold))}`}
        >
          {formatDelta(planned - threshold.limit, deltaDecimals)}
        </div>
      ) : (
        <div />
      )}
    </>
  );
};
